/**
 * GS-05 Confirm schedule: a dates x reps grid on the event's Confirm Schedule tab. For each date of a multi-day event
 * the operator ticks who is working; Save writes the whole set through set_event_staffing (planStaffingFromGrid mirrors
 * its rules, and every refusal's sentence is shown).
 *
 * Only for events in the MSR date model (their days are event_days rows and staffing is per day). For any other event
 * the server refuses per-day rows (STAFFING_REQUIRES_DATE_MODEL), so the grid explains that every rep on the list
 * works every day instead of offering a choice it could not save.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  describeDateModelError,
  getEventStaffing,
  isDateModelActive,
  setEventStaffingExact,
} from "../../services/eventDateModel.service";
import { getEventDaysFor } from "../../services/msrPortal.service";
import { formatServiceDate } from "../../utils/msrEventFormat";
import {
  planStaffingFromGrid,
  selectionChanged,
  selectionFromStaffing,
  type GridDay,
  type GridSelection,
} from "../../utils/staffingGrid";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";
import { useTimeOff } from "../../hooks/useTimeOff";
import { timeOffSentence } from "../../services/timeOffConflicts";

interface Props {
  eventId: string;
  /** position id -> display name (from the Sales Reps list rows). */
  names: Record<string, string>;
  canEdit: boolean;
}

const ConfirmScheduleGrid: React.FC<Props> = ({ eventId, names, canEdit }) => {
  const qc = useQueryClient();
  const { showToast } = useToast();

  const model = useQuery({
    queryKey: ["msr-date-model-active", eventId],
    queryFn: () => isDateModelActive(eventId),
  });
  const inModel = model.data === true;

  const staffing = useQuery({
    queryKey: ["event-staffing", eventId],
    queryFn: () => getEventStaffing(eventId),
    enabled: inModel,
  });
  const dayRows = useQuery({
    queryKey: ["event-days", eventId],
    // the grid must not mistake a failed read for a one-day event (internal audit P3-4)
    queryFn: () => getEventDaysFor([eventId], { throwOnError: true }),
    enabled: inModel,
  });

  const days: GridDay[] = useMemo(
    () =>
      (dayRows.data ?? [])
        .filter((d) => d.id)
        .map((d) => ({ id: d.id as string, date: d.service_date }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    [dayRows.data],
  );
  const assignments = useMemo(
    () => staffing.data?.assignments ?? [],
    [staffing.data],
  );
  const initial = useMemo(
    () => selectionFromStaffing(assignments, days),
    [assignments, days],
  );
  const [selection, setSelection] = useState<GridSelection>(initial);
  const [error, setError] = useState<string | null>(null);
  // A reload (after a save, a refusal or someone else's change) shows the stored schedule; a refusal's sentence stays.
  useEffect(() => {
    setSelection(initial);
  }, [initial]);

  // MSR QA 10-20: time off per rep on these days -> a badge in the cell, and Save asks first when a newly ticked day
  // is a rep's day off (warning only; the save still goes through on "Save anyway").
  const { data: timeOff = {} } = useTimeOff(
    assignments.map((a) => a.position_id),
    days.map((d) => d.date),
  );
  const isOff = (pos: string, dayId: string) =>
    (timeOff[pos] ?? []).includes(days.find((d) => d.id === dayId)?.date ?? "");
  const [timeOffPrompt, setTimeOffPrompt] = useState<string | null>(null);
  const nameOf = (pos: string) => names[pos] ?? "this rep";
  const labelOf = (dayId: string) =>
    formatServiceDate(days.find((d) => d.id === dayId)?.date ?? "");

  const save = useMutation({
    mutationFn: async () => {
      if (!staffing.data) {
        throw new Error("The schedule is still loading.");
      }
      const plan = planStaffingFromGrid(
        assignments,
        selection,
        days,
        staffing.data.day_closers,
        nameOf,
        labelOf,
      );
      if (!plan.ok) {
        throw new Error(plan.error);
      }
      await setEventStaffingExact(
        eventId,
        plan.staffing,
        staffing.data.version,
      );
    },
    onSuccess: async () => {
      setError(null);
      showToast("success", "Schedule saved");
      // Save stays pending until the new version is loaded, so a second click cannot send the old version
      // (internal audit P3-3).
      await qc.invalidateQueries({ queryKey: ["event-staffing", eventId] });
      for (const queryKey of [
        ["event-sales-assignments", eventId],
        ["event-sales-assignment-days", eventId],
        ["sales-calendar"],
        ["event-day-run-state", eventId],
      ]) {
        void qc.invalidateQueries({ queryKey });
      }
    },
    onError: (e: unknown) => {
      setError(
        describeDateModelError(e) ??
          (e as { message?: string } | null)?.message ??
          "The schedule could not be saved.",
      );
      // a version conflict or a server refusal: show the current schedule
      void qc.invalidateQueries({ queryKey: ["event-staffing", eventId] });
    },
  });

  if (model.isLoading) {
    return null;
  }
  if (!inModel) {
    return (
      <p
        className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
        data-testid="confirm-schedule-not-in-model"
      >
        Everyone on this list works every day of the event. Choosing reps date
        by date is available for events that are started and closed day by day;
        this event isn't run that way yet.
      </p>
    );
  }
  if (staffing.isLoading || dayRows.isLoading) {
    return <p className="text-sm text-gray-400">Loading the schedule…</p>;
  }
  if (staffing.error || dayRows.error) {
    return (
      <p className="text-sm text-red-700">
        The schedule could not be loaded. Reload the page to try again.
      </p>
    );
  }
  if (assignments.length === 0) {
    return null;
  }
  if (days.length < 2) {
    return (
      <p
        className="text-sm text-gray-600"
        data-testid="confirm-schedule-one-day"
      >
        This is a one-day event: everyone on the list works it.
      </p>
    );
  }

  const leadFirst = (r: string) => (r === "lead" ? 0 : 1);
  const ordered = [...assignments].sort(
    (a, b) =>
      leadFirst(a.role) - leadFirst(b.role) ||
      nameOf(a.position_id).localeCompare(nameOf(b.position_id)),
  );
  const changed = selectionChanged(initial, selection) && !staffing.isFetching;
  const leadPerDay = assignments.some(
    (a) => a.role === "lead" && a.day_scope === "selected",
  );
  const toggle = (pos: string, day: string, on: boolean) =>
    setSelection((cur) => {
      const list = new Set(cur[pos] ?? []);
      if (on) {
        list.add(day);
      } else {
        list.delete(day);
      }
      return { ...cur, [pos]: [...list] };
    });

  return (
    <div className="space-y-3" data-testid="confirm-schedule-grid">
      <div>
        <h4 className="text-sm font-semibold text-gray-800">
          Who's working each day
        </h4>
        <p className="text-xs text-gray-500">
          Tick the days each rep works. The Lead works every day and can close
          any day. A rep ticked on every day also works days added later.
        </p>
      </div>
      {leadPerDay && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
          The Lead is set to work only some days. Saving this schedule puts the
          Lead on every day.
        </p>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">
                Rep
              </th>
              {days.map((d) => (
                <th
                  key={d.id}
                  className="whitespace-nowrap px-3 py-2 text-center font-medium text-gray-600"
                >
                  {formatServiceDate(d.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {ordered.map((a) => {
              const isLead = a.role === "lead";
              return (
                <tr key={a.position_id}>
                  <td className="sticky left-0 whitespace-nowrap bg-white px-3 py-2 text-gray-900">
                    {nameOf(a.position_id)}
                    {isLead && (
                      <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-800">
                        Lead
                      </span>
                    )}
                  </td>
                  {days.map((d) => {
                    const on =
                      isLead || (selection[a.position_id] ?? []).includes(d.id);
                    return (
                      <td key={d.id} className="px-3 py-2 text-center">
                        {isOff(a.position_id, d.id) && (
                          <span
                            className="mb-1 block rounded bg-amber-100 px-1 text-[11px] font-medium text-amber-900"
                            data-testid="time-off-badge"
                          >
                            Not available
                          </span>
                        )}
                        <input
                          type="checkbox"
                          className="h-5 w-5"
                          checked={on}
                          disabled={isLead || !canEdit || save.isPending}
                          onChange={(e) =>
                            toggle(a.position_id, d.id, e.target.checked)
                          }
                          aria-label={`${nameOf(a.position_id)} works ${formatServiceDate(d.date)}`}
                          title={
                            isLead
                              ? "The Lead works every day. Change the Lead with Assign Rep."
                              : undefined
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={!changed || save.isPending}
            onClick={() => {
              setSelection(initial);
              setError(null);
            }}
            className="min-h-[44px] rounded-md border border-gray-300 bg-white px-4 text-sm disabled:opacity-50"
          >
            Undo changes
          </button>
          <button
            type="button"
            disabled={!changed || save.isPending}
            onClick={() => {
              // newly ticked (pos, day) pairs that are a rep's day off
              const hits: Record<string, string[]> = {};
              for (const [pos, dayIds] of Object.entries(selection)) {
                for (const dayId of dayIds) {
                  if (
                    !(initial[pos] ?? []).includes(dayId) &&
                    isOff(pos, dayId)
                  ) {
                    (hits[pos] ??= []).push(
                      days.find((d) => d.id === dayId)?.date ?? "",
                    );
                  }
                }
              }
              const lines = Object.entries(hits).map(([pos, ds]) =>
                timeOffSentence(nameOf(pos), ds.sort()),
              );
              if (lines.length) {
                setTimeOffPrompt(`${lines.join(" ")} Save anyway?`);
                return;
              }
              save.mutate();
            }}
            className="min-h-[44px] rounded-md bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save schedule"}
          </button>
        </div>
      )}
      <ConfirmDialog
        isOpen={timeOffPrompt !== null}
        onClose={() => setTimeOffPrompt(null)}
        onConfirm={() => {
          setTimeOffPrompt(null);
          save.mutate();
        }}
        title="A rep isn't marked available"
        message={timeOffPrompt ?? ""}
        confirmText="Save anyway"
        variant="warning"
      />
    </div>
  );
};

export default ConfirmScheduleGrid;
